import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView, track } from "@/lib/analytics";

/**
 * Centralized GA4 bridge. Renders nothing.
 *  - Initializes the single Google tag exactly once.
 *  - Sends one page_view per SPA route change (deduped, waits a tick for the title to update).
 *  - Fires scroll_90 once per page and engaged_session once per session.
 */
export default function Analytics() {
  const { pathname, search } = useLocation();
  const lastPage = useRef(null);
  const inited = useRef(false);

  useEffect(() => {
    if (!inited.current) { initAnalytics(); inited.current = true; }
  }, []);

  // page_view on every route change, deduped by full path+query.
  useEffect(() => {
    const url = pathname + search;
    if (lastPage.current === url) return;
    lastPage.current = url;
    const id = setTimeout(() => trackPageView(url, document.title), 80);
    return () => clearTimeout(id);
  }, [pathname, search]);

  // scroll_90 — once per page (effect re-runs on route change, resetting the flag).
  useEffect(() => {
    let fired = false;
    const onScroll = () => {
      const h = document.documentElement;
      const denom = h.scrollHeight - h.clientHeight;
      if (denom <= 0) return;
      if (h.scrollTop / denom >= 0.9 && !fired) { fired = true; track.scroll90(pathname + search); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname, search]);

  // engaged_session — once per browser session (10s dwell or first interaction).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { if (sessionStorage.getItem("klyroo_engaged")) return; } catch { /* ignore */ }
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      try { sessionStorage.setItem("klyroo_engaged", "1"); } catch { /* ignore */ }
      track.engagedSession();
      cleanup();
    };
    const t = setTimeout(fire, 10000);
    const cleanup = () => {
      clearTimeout(t);
      window.removeEventListener("scroll", fire);
      window.removeEventListener("click", fire);
    };
    window.addEventListener("scroll", fire, { passive: true, once: true });
    window.addEventListener("click", fire, { once: true });
    return cleanup;
  }, []);

  return null;
}
